import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { breakpoint } from '@zocdoc/components/src/grid';
import { kievitSlabBook } from '@zocdoc/components/src/typography';
import Button from '@zocdoc/components/src/button';
import UnstyledSpinner from '@zocdoc/components/src/Spinner';
import { grey50 } from '@zocdoc/branding/colors';
import { LOCALIZED_STRINGS } from '../constants';
import Review from '../Review';
import { debounce } from 'lodash';

const shouldTrackScroll = index => {
    // Track at <10, then 20, 30, 40, 50
    return index < 10 || (index <= 50 && !(index % 10));
};

const REVIEW_BATCH_SIZE = 100;
export default class ReviewsBody extends PureComponent {
    static propTypes = {
        defaultNumberOfReviews: PropTypes.number.isRequired,
        reviewCount: PropTypes.number.isRequired,
        reviews: PropTypes.array,
        showAll: PropTypes.bool.isRequired,
        trackProfileScrollCount: PropTypes.func.isRequired,
    }

    static defaultProps = {
        reviews: [],
    }

    constructor(props) {
        super(props);
        this.state = {
            renderedReviews: this.props.reviews || [],
        };

        this._currentNumberOfRenderedReviews = props.defaultNumberOfReviews;
        this._renderLoop = null;

        this._maxScrollValue = -1;
        this.handleTrackProfileScrollCount = debounce(this._handleTrackProfileScrollCount);
    }

    componentDidUpdate(prevProps) {
        const {
            defaultNumberOfReviews,
            showAll,
            reviews,
        } = this.props;

        if (prevProps.reviews.length !== reviews.length) {
            if (showAll && reviews.length > defaultNumberOfReviews) {
                // Delay rendering of large list of reviews
                this._renderLoop = setInterval(this.updateBatchOfReviews, 100);
            } else {
                this.setState({
                    renderedReviews: this.props.reviews,
                });
            }
        }
    }

    updateBatchOfReviews = () => {
        this._currentNumberOfRenderedReviews += REVIEW_BATCH_SIZE;
        this.setState({
            renderedReviews: this.props.reviews.slice(0, this._currentNumberOfRenderedReviews),
        });
        if (this._currentNumberOfRenderedReviews > this.props.reviews.length) {
            clearInterval(this._renderLoop);
        }
    }

    _handleTrackProfileScrollCount = () => {
        this.props.trackProfileScrollCount(this._maxScrollValue);
    }

    handleScrollIntoReview = index => {
        // Scroll listeners to track how many reviews user sees
        if (this._maxScrollValue < index) {
            this._maxScrollValue = index;
            this.handleTrackProfileScrollCount();
        }
    }


    scrollToTop = () => {
        const { clickBackToTop } = this.props;
        clickBackToTop();
        window.scrollTo(0, 0);
    }

    maybeRenderShowMoreButton() {
        const {
            showAll,
            handleClickShowMoreReviews,
            reviewCount,
            reviews = [],
        } = this.props;

        if (reviews.length === reviewCount) {
            // All reviews have been loaded
            return;
        }

        const disabled = showAll;

        return (
            <ShowAllReviewsButtonWrapper>
                <ShowAllReviewsButton
                    data-test="show-more-button"
                    disabled={disabled}
                    onClick={disabled ? undefined : handleClickShowMoreReviews}
                >
                    <ButtonText>
                        <FormattedMessage {...LOCALIZED_STRINGS.ShowMoreReviews} />
                    </ButtonText>
                </ShowAllReviewsButton>
                <Spinner
                    hidden={!disabled}
                    size={20}
                />
            </ShowAllReviewsButtonWrapper>
        );
    }

    render() {
        const {
            renderedReviews,
        } = this.state;

        return (
            <ReviewsBodyContainer>
                <div>
                    {renderedReviews.map((review, index) => {
                        const onScrolledIntoView = shouldTrackScroll(index) ?
                            this.handleScrollIntoReview : null;
                        return (
                            <Review
                                key={index}
                                index={index}
                                onScrolledIntoView={onScrolledIntoView}
                                {...review}
                            />
                        );
                    })}
                </div>
                {this.maybeRenderShowMoreButton()}
                <ButtonWrapper>
                    <Button
                        data-test="back-to-top-button"
                        onClick={this.scrollToTop}
                        style={Button.STYLE.SCROLL_UP_ARROW}
                    >
                        <FormattedMessage {...LOCALIZED_STRINGS.BackToTop} />
                    </Button>
                </ButtonWrapper>
            </ReviewsBodyContainer>
        );
    }
}

const ReviewsBodyContainer = styled.div`
    padding-top: 25px;
`;

const ShowAllReviewsButtonWrapper = styled.div`
    align-items: center;
    display: flex;
    margin: 8px 0 28px;
`;

const ShowAllReviewsButton = styled.div`
    border-bottom: 1px dotted ${p => p.theme.profile.linkColor};
    color: ${p => p.theme.profile.linkColor};
    cursor: pointer;

    ${p => p.disabled && css`
        border-bottom-color: ${grey50};
        color: ${grey50};
    `}
`;

const ButtonText = styled.div`
    ${kievitSlabBook(14, 20)};
    ${breakpoint.medium} {
        ${kievitSlabBook(18, 22)};
    }
`;

const ButtonWrapper = styled.div`
    clear: both;
`;

const Spinner = styled(UnstyledSpinner)`
    display: inline-block;
    position: static;
    margin-left: 12px;
`;
